/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useRef } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  Text,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon, AddIcon } from '@chakra-ui/icons';

export default function CreatableDropdown({
  options = [],
  value,
  onChange,
  onCreate,
  placeholder = 'Select',
  bg,
  color,
  borderColor,
  isDisabled = false,
  width = '100%',
  isClearable = true,
  onMenuOpen,
  searchCreate = true,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const menuButtonRef = useRef(null);

  const menuBg = useColorModeValue('white', 'gray.800');
  const menuBorder = useColorModeValue('gray.200', 'gray.700');
  const itemActiveBg = useColorModeValue('blue.50', 'blue.900');
  const itemHoverBg = useColorModeValue('blue.100', 'blue.800');
  const itemText = useColorModeValue('gray.800', 'gray.100');

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const hasValue = value !== undefined && value !== null && value !== '';

  // 🔹 filter options
  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      opt.label?.toString().toLowerCase().includes(search.toLowerCase()),
    );
  }, [options, search]);

  const exactMatch = options.some(
    (o) => o.label?.toString().toLowerCase() === search.toLowerCase(),
  );

  const showCreate = search && !exactMatch;

  // Handle menu open
  const handleMenuOpen = () => {
    if (onMenuOpen) {
      onMenuOpen();
    }
    onOpen();
  };

  // Handle clear - FIXED VERSION
  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear the value
    onChange?.('');
    setSearch('');

    // Close menu if open
    if (isOpen) {
      onClose();
    }

    // Prevent the menu from opening
    setTimeout(() => {
      if (menuButtonRef.current) {
        menuButtonRef.current.blur();
      }
    }, 0);
  };

  return (
    <Menu
      isLazy
      strategy="fixed"
      isOpen={isOpen}
      onOpen={handleMenuOpen}
      onClose={onClose}
    >
      <MenuButton
        as={Box}
        w={width}
        isDisabled={isDisabled}
        ref={menuButtonRef}
      >
        <InputGroup>
          <Input
            readOnly
            value={selectedLabel}
            placeholder={placeholder}
            bg={bg}
            color={color}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="lg"
            cursor="pointer"
            pr="3rem"
            _focus={{
              boxShadow: 'outline',
              borderColor: 'blue.500',
            }}
          />

          <InputRightElement w="3rem" pointerEvents="auto" zIndex={2}>
            <Box display="flex" alignItems="center" gap={2}>
              {isClearable && hasValue && (
                <Box
                  as="button"
                  type="button"
                  aria-label="Clear selection"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.('');
                    // Force close menu
                    if (isOpen) {
                      onClose();
                    }
                  }}
                  onMouseDown={(e) => {
                    // This prevents the menu from opening
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  _hover={{ color: 'red.500' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="16px"
                  h="16px"
                >
                  <CloseIcon boxSize="10px" />
                </Box>
              )}

              <ChevronDownIcon
                transition="transform 0.25s"
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                boxSize={4}
              />
            </Box>
          </InputRightElement>
        </InputGroup>
      </MenuButton>

      <MenuList
        maxH="260px"
        overflowY="auto"
        p={2}
        bg={menuBg}
        borderColor={menuBorder}
        boxShadow="lg"
        onMouseDown={(e) => e.stopPropagation()} // Prevent menu close issues
      >
        {/* 🔍 Search / Create input */}
        {searchCreate && (
          <Input
            size="sm"
            mb={2}
            placeholder="Type to search or add..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && showCreate) {
                onCreate?.(search);
                onChange?.(search);
                setSearch('');
                onClose();
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {/* ➕ Create option */}
        {showCreate && (
          <MenuItem
            icon={<AddIcon />}
            color="blue.500"
            onClick={() => {
              onCreate?.(search);
              onChange?.(search);
              setSearch('');
              onClose();
            }}
            _hover={{ bg: 'blue.50' }}
          >
            Add "{search}"
          </MenuItem>
        )}

        {/* Existing options */}
        {filteredOptions.map((opt) => {
          const isActive = opt.value === value;

          return (
            <MenuItem
              key={opt.value}
              bg={isActive ? itemActiveBg : 'transparent'}
              color={itemText}
              _hover={{ bg: itemHoverBg }}
              onClick={() => {
                onChange?.(opt.value);
                setSearch('');
                onClose();
              }}
            >
              {opt.label}
            </MenuItem>
          );
        })}

        {!filteredOptions.length && !showCreate && (
          <Text px={3} py={2} fontSize="sm" color="gray.500">
            No options found
          </Text>
        )}
      </MenuList>
    </Menu>
  );
}
